import { AlertOutlined, LeftOutlined, SendOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Input, message, Select, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { getClients } from "@/modules/clients/services/clients.backend";
import type { ClientDetail } from "@/modules/clients/types/clients.types";
import type { AppRole } from "@/shared/config/navigation";
import { canSendManualSms } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { getSmsTemplates, sendBulkSms, sendSms } from "../services/sms.api";

function formatMessageTemplate(body: string, client?: ClientDetail): string {
	if (!client) {
		return body;
	}
	const clientFullName = [client.lastName, client.firstName, client.patronymic]
		.filter(Boolean)
		.join(" ");
	return body
		.replace(/{client_name}/g, clientFullName)
		.replace(/{phone}/g, client.phone)
		.replace(/{manager_name}/g, client.managerName || "menejer")
		.replace(/{room_number}/g, client.roomNumber || "-")
		.replace(/{building_name}/g, client.buildingName || "-")
		.replace(/{amount}/g, client.activeSale?.monthlyPayment || "0.50 so'm")
		.replace(/{due_date}/g, client.activeSale?.nextPaymentDate || "2026-07-01");
}

export function SmsSendPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const [searchParams] = useSearchParams();
	const queryClientId = searchParams.get("clientId");
	const canSendSms = canSendManualSms(role);

	const [mode, setMode] = useState<"single" | "batch">("single");
	const [clientId, setClientId] = useState<string | undefined>(queryClientId || undefined);
	const [templateId, setTemplateId] = useState<string>("custom");
	const [groupName, setGroupName] = useState("Iyun oyi eslatmasi");
	const [recipientGroup, setRecipientGroup] = useState("overdue");
	const [messageText, setMessageText] = useState("");

	const fromDashboard = (location.state as { fromDashboard?: boolean })?.fromDashboard === true;

	// 1. Fetch real clients
	const { data: clientsData, isLoading: clientsLoading } = useQuery({
		queryKey: ["clients"],
		queryFn: () => getClients({ limit: 100 }),
	});

	const clients = useMemo(() => clientsData?.items ?? [], [clientsData]);

	// 2. Fetch real templates
	const { data: templatesData, isLoading: templatesLoading } = useQuery({
		queryKey: ["smsTemplates"],
		queryFn: () => getSmsTemplates({ limit: 100 }),
	});

	const templates = useMemo(() => templatesData?.items ?? [], [templatesData]);

	// Find selected client
	const selectedClient = useMemo(() => {
		if (clientId) {
			return clients.find((c) => c.id === clientId);
		}
		return clients[0];
	}, [clients, clientId]);

	// Setup initial clientId when clients load
	useEffect(() => {
		if (clients.length > 0 && !clientId) {
			setClientId(clients[0].id);
		}
	}, [clients, clientId]);

	// Template options dropdown
	const templateOptions = useMemo(() => {
		const options = templates.map((t) => ({
			label: t.name,
			value: t.id,
		}));
		return [...options, { label: "Ixtiyoriy (Custom)", value: "custom" }];
	}, [templates]);

	// Recipient options list for batch sending
	const recipientOptions = [
		{ label: "Kechikkan to'lovi bor mijozlar", value: "overdue" },
		{ label: "Iyul to'lovi yaqin mijozlar", value: "july" },
		{ label: "Barcha mijozlar", value: "all" },
	];

	// Filter clients for batch send preview
	const batchRecipients = useMemo(() => {
		if (recipientGroup === "overdue") {
			return clients.filter((c) => c.debtCount > 0);
		}
		if (recipientGroup === "july") {
			return clients.slice(0, Math.min(18, clients.length)); // Fallback selection
		}
		return clients;
	}, [clients, recipientGroup]);

	// Update message when selected client or template changes
	useEffect(() => {
		if (!selectedClient) {
			return;
		}

		if (templateId === "custom") {
			setMessageText(`${selectedClient.firstName}, `);
		} else {
			const template = templates.find((t) => t.id === templateId);
			if (template) {
				setMessageText(formatMessageTemplate(template.body, selectedClient));
			}
		}
	}, [selectedClient, templateId, templates]);

	// 3. Send single mutation
	const sendSmsMutation = useMutation({
		mutationFn: (payload: { clientId: string; messageBody: string; templateId?: string }) =>
			sendSms(payload),
		onSuccess: () => {
			message.success("SMS yuborildi");
			navigate(role === "manager" ? "/dashboard" : "/sms/balance");
		},
		onError: (err) => {
			message.error(parseApiError(err) || "SMS yuborishda xatolik");
		},
	});

	// 4. Send bulk mutation
	const sendBulkSmsMutation = useMutation({
		mutationFn: (payload: { clientIds: string[]; messageBody: string; templateId?: string }) =>
			sendBulkSms(payload),
		onSuccess: (res) => {
			message.success(
				`Ommaviy SMS yuborish boshlandi. Jami: ${res.totalCount}, Yuborildi: ${res.sentCount}`
			);
			navigate(role === "manager" ? "/dashboard" : "/sms/balance");
		},
		onError: (err) => {
			message.error(parseApiError(err) || "Ommaviy SMS yuborishda xatolik");
		},
	});

	const handleClientChange = (value: string) => {
		setClientId(value);
	};

	const handleTemplateChange = (value: string) => {
		setTemplateId(value);
	};

	const handleSend = () => {
		if (!canSendSms) {
			message.warning("Bu rol qo'lda SMS yubora olmaydi.");
			return;
		}

		if (!messageText.trim()) {
			message.warning("Xabar matnini kiriting");
			return;
		}

		const tid = templateId === "custom" ? undefined : templateId;

		if (mode === "single") {
			if (!clientId) {
				message.warning("Mijozni tanlang");
				return;
			}
			sendSmsMutation.mutate({
				clientId,
				messageBody: messageText,
				templateId: tid,
			});
		} else {
			const clientIds = batchRecipients.map((c) => c.id);
			if (clientIds.length === 0) {
				message.warning("Qabul qiluvchilar ro'yxati bo'sh");
				return;
			}
			sendBulkSmsMutation.mutate({
				clientIds,
				messageBody: messageText,
				templateId: tid,
			});
		}
	};

	const backPath = role === "manager" ? "/dashboard" : "/reports/debts";
	const backLabel = role === "manager" ? "Bosh sahifaga qaytish" : "Qarzdorlikka qaytish";
	const handleBack = () => {
		if (fromDashboard) {
			navigate(-1);
			return;
		}
		navigate(backPath);
	};

	if (clientsLoading || templatesLoading) {
		return (
			<div style={{ textAlign: "center", padding: "100px 0" }}>
				<Spin size="large" />
				<div style={{ marginTop: "16px", color: "var(--crm-ink-3)" }}>
					Ma'lumotlar yuklanmoqda...
				</div>
			</div>
		);
	}

	return (
		<div className="crm-sms-send-page">
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={handleBack}
						type="text"
					>
						{fromDashboard ? "Bosh sahifaga qaytish" : backLabel}
					</Button>
					<h1 style={{ marginTop: "4px" }}>
						{mode === "batch" ? "Ommaviy SMS (batch)" : "Qo'lda SMS yuborish"}
					</h1>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						{mode === "batch"
							? "Tanlangan guruhga xabar yuborish"
							: "Mijozga tezkor xabar yuborish"}
					</p>
				</div>
				<div className="crm-sms-mode">
					<button
						className={mode === "single" ? "active" : ""}
						type="button"
						onClick={() => setMode("single")}
					>
						Bitta
					</button>
					<button
						className={mode === "batch" ? "active" : ""}
						type="button"
						onClick={() => setMode("batch")}
					>
						Ommaviy
					</button>
				</div>
			</div>

			<div className="crm-sms-send-grid">
				<section className="crm-card crm-sms-send-card">
					<div className="crm-card-head">
						<h3>{mode === "batch" ? "Yuborish guruhi" : "Bitta mijozga"}</h3>
					</div>
					{mode === "single" ? (
						selectedClient ? (
							<div className="crm-card-body">
								<div className="crm-field">
									<span>Mijoz</span>
									<Select
										showSearch
										filterOption={(input, option) =>
											(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
										}
										options={clients.map((client) => ({
											label: [client.lastName, client.firstName, client.patronymic]
												.filter(Boolean)
												.join(" "),
											value: client.id,
										}))}
										value={selectedClient.id}
										onChange={handleClientChange}
									/>
								</div>

								<div className="crm-field">
									<span>Telefon</span>
									<Input value={selectedClient.phone} readOnly />
								</div>

								<div className="crm-field">
									<span>Shablon</span>
									<Select
										options={templateOptions}
										value={templateId}
										onChange={handleTemplateChange}
									/>
								</div>

								<div className="crm-field">
									<span>Xabar matni</span>
									<Input.TextArea
										rows={2}
										value={messageText}
										onChange={(event) => setMessageText(event.target.value)}
									/>
								</div>

								<div className="crm-sms-send-meta">
									<span>{Math.ceil(messageText.length / 160)} qism / 50 tiyin</span>
									<span>
										Balansdan {((Math.ceil(messageText.length / 160) * 50) / 100).toFixed(2)} so'm
									</span>
								</div>

								<div className="crm-sms-send-footer">
									<Button
										className="crm-primary-btn"
										icon={<SendOutlined />}
										type="primary"
										loading={sendSmsMutation.isPending}
										onClick={handleSend}
									>
										Yuborish
									</Button>
								</div>
							</div>
						) : (
							<div className="crm-card-body" style={{ textAlign: "center", padding: "40px" }}>
								Mijozlar ro'yxati bo'sh. SMS yuborish uchun mijoz yaratishingiz kerak.
							</div>
						)
					) : (
						<div className="crm-card-body">
							<div className="crm-field">
								<span>Guruh nomi</span>
								<Input value={groupName} onChange={(event) => setGroupName(event.target.value)} />
							</div>

							<div className="crm-field">
								<span>Shablon</span>
								<Select
									options={templateOptions}
									value={templateId}
									onChange={handleTemplateChange}
								/>
							</div>

							<div className="crm-field">
								<span>Qabul qiluvchilar</span>
								<Select
									options={recipientOptions}
									value={recipientGroup}
									onChange={setRecipientGroup}
								/>
							</div>

							<div className="crm-sms-batch-summary">
								<div>
									<span>Qabul qiluvchilar</span>
									<strong>{batchRecipients.length} ta</strong>
								</div>
								<div>
									<span>Jami narx</span>
									<strong>{((batchRecipients.length * 50) / 100).toFixed(2)} so'm</strong>
								</div>
							</div>

							<div className="crm-sms-send-footer">
								<Button
									className="crm-primary-btn"
									icon={<SendOutlined />}
									type="primary"
									loading={sendBulkSmsMutation.isPending}
									onClick={handleSend}
								>
									Yuborish ({batchRecipients.length})
								</Button>
							</div>
						</div>
					)}
				</section>

				{mode === "single" ? (
					<aside className="crm-sms-send-side">
						<div className="crm-card crm-sms-warning-card">
							<strong>
								<AlertOutlined /> Balans yetmasa
							</strong>
							<p>Balans yetarli bo'lmasa, xabar yuborilmaydi va tizim ogohlantiradi.</p>
						</div>

						<div className="crm-card crm-sms-flow-card">
							<strong>Yuborish oqimi</strong>
							<p>
								Xabar yuborilgach balans yangilanadi, holati esa yetkazilganidan keyin ko'rinadi.
							</p>
						</div>
					</aside>
				) : (
					<aside className="crm-card crm-sms-batch-list">
						<div className="crm-card-head">
							<h3>Tanlangan ro'yxat ({batchRecipients.length} ta)</h3>
						</div>
						<div style={{ maxHeight: "300px", overflowY: "auto" }}>
							<table>
								<thead>
									<tr>
										<th>Mijoz</th>
										<th>Telefon</th>
										<th>Shartnoma holati</th>
									</tr>
								</thead>
								<tbody>
									{batchRecipients.slice(0, 10).map((client) => (
										<tr key={client.id}>
											<td>
												{client.firstName} {client.lastName.charAt(0)}.
											</td>
											<td>{client.phone}</td>
											<td>
												<span
													className={`crm-badge ${client.status === "active" ? "green" : "slate"}`}
												>
													{client.status === "active" ? "Faol" : "Faol emas"}
												</span>
											</td>
										</tr>
									))}
									{batchRecipients.length > 10 && (
										<tr>
											<td colSpan={3} style={{ textAlign: "center", color: "var(--crm-ink-3)" }}>
												... yana {batchRecipients.length - 10} ta mijoz
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
						<p className="crm-sms-batch-note" style={{ marginTop: "12px" }}>
							<AlertOutlined /> Jami narx balansdan oshsa, xabarlar yuborilmaydi.
						</p>
					</aside>
				)}
			</div>
		</div>
	);
}

export default SmsSendPage;
